import type { FeedEntitlementRow, FeedFieldDifference, FeedDifference, UpdatedFeedEntitlementRow } from '../entitlements-types';

function getEntitlementDifference(
  existingEntitlement: FeedEntitlementRow,
  updatedEntitlement: FeedEntitlementRow
): Record<keyof FeedEntitlementRow, FeedFieldDifference> | null {
  const difference: Record<keyof FeedEntitlementRow, FeedFieldDifference> = {};

  Object.keys(existingEntitlement).forEach((key) => {
    if (key === 'isEdited') {
      return;
    }

    if (existingEntitlement[key] !== updatedEntitlement[key]) {
      difference[key] = { previous: existingEntitlement[key], change: updatedEntitlement[key] };
    }
  });

  return Object.keys(difference).length ? difference : null;
}

export function buildFeedDifference(
  savedEntitlements: FeedEntitlementRow[] = [],
  draftEntitlements: FeedEntitlementRow[] = [],
  permissionId: string
): FeedDifference {
  const combined = [...savedEntitlements, ...draftEntitlements];
  const result: FeedDifference = { created: {}, updated: {}, deleted: {} };

  for (const current of combined) {
    const existing = savedEntitlements.find(({ id }) => id === current.id);
    const included = draftEntitlements.find(({ id }) => id === current.id);

    if (!existing && included) {
      const { id, name, feedId, startDate, endDate, isOngoing, onGoingMaxDate, isDayEntitlement, expiryDate } = included;

      const createdFeed: FeedEntitlementRow = {
        id,
        name,
        feedId,
        startDate,
        endDate,
        isOngoing,
        onGoingMaxDate: onGoingMaxDate || null,
        isDayEntitlement,
        expiryDate: expiryDate || null,
        permissionId: permissionId ?? included.permissionId,
      };

      result.created[id] = createdFeed;
      continue;
    }

    if (existing && !included) {
      result.deleted[current.id] = current;
      continue;
    }

    if (existing && included) {
      const difference = getEntitlementDifference(existing, included);
      if (difference) {
        const { name, startDate, endDate, isOngoing, onGoingMaxDate, isDayEntitlement, expiryDate } = included;
        const updatedFeed: UpdatedFeedEntitlementRow = {
          id: existing.id,
          name,
          startDate,
          endDate,
          isOngoing,
          onGoingMaxDate: onGoingMaxDate || null,
          isDayEntitlement,
          expiryDate: expiryDate || null,
          difference,
        };

        result.updated[current.id] = updatedFeed;
      }
    }
  }

  return result;
}
